import React from 'react'

import { UnstyledButton } from '../../components/UnstyledButton'
import { CssVars, NeutralColorToken } from '../themes'
import { FoundationIcon } from './FoundationIcon'
import { ListBox, type Props as ListBoxProps } from './internal/ListBox'
import {
  isMenuOptionSelectable,
  type MenuOptionVariantSelectable,
} from './MenuOption'
import { getStylesForTextType, Type } from './Type'

type Props<
  TOption extends MenuOptionVariantSelectable = MenuOptionVariantSelectable
> = Omit<ListBoxProps<TOption>, 'children'>

/**
 * The `SelectMenu` is a dropdown that allows the user to select from a list of options.
 * Each option must have a unique `value` and a human-readable `label`.
 */
export function SelectMenu<
  TOption extends MenuOptionVariantSelectable = MenuOptionVariantSelectable
>(props: Props<TOption>) {
  return (
    <ListBox<TOption> {...props}>
      {(toggleProps, isMenuOpen, selectedOption) => {
        const isDisabled = props.state === 'disabled'
        const isLocked = props.state === 'locked'
        const isDisabledOrLocked = isDisabled || isLocked

        let contentColor
        // Not adding an override for 'locked' state as we want it to maintain its normal color
        if (isDisabled) {
          contentColor = NeutralColorToken.Fg3
        } else if (!selectedOption && props.placeholder) {
          contentColor = NeutralColorToken.Fg2
        }

        return (
          <UnstyledButton
            {...toggleProps}
            css={[
              {
                alignItems: 'center',
                backgroundColor: CssVars.neutral.bg1,
                borderColor:
                  props.state === 'error'
                    ? CssVars.semantic.error.base
                    : CssVars.neutral.bg3,
                borderRadius: CssVars.size.borderRadius,
                borderStyle: 'solid',
                borderWidth: 1,
                display: 'flex',
                height: CssVars.size.cellHeightSm,
                justifyContent: 'space-between',
                paddingLeft: CssVars.size.gutterWidth,
                paddingRight: CssVars.size.gutterWidthSm,
                width: '100%',
              },

              !isDisabledOrLocked && [
                isMenuOpen && {
                  borderColor: CssVars.semantic.primary.base,
                },
                !isMenuOpen && {
                  '&:focus': {
                    borderColor: CssVars.semantic.primary.base,
                    outline: 0,
                  },
                  '&:hover': {
                    borderColor: CssVars.neutral.fg3,
                  },
                },
              ],

              isDisabledOrLocked && {
                borderColor: CssVars.neutral.bg3,
                cursor: 'not-allowed',
              },
            ]}
          >
            <div
              css={[
                {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
                // NOTE(jare): Make sure to apply the same typography styles to this wrapping
                // div to make sure that the style of the ellipsis rendered when the text
                // overflows matches the style of the rendered text
                getStylesForTextType({ isBold: false, size: 'S' }),
              ]}
            >
              <Type.Text color={contentColor} size="S">
                {selectedOption
                  ? (isMenuOptionSelectable(selectedOption) &&
                      selectedOption.controlLabel) ||
                    selectedOption.label
                  : props.placeholder}
              </Type.Text>
            </div>
            <div
              css={{
                display: 'flex',
                flexGrow: 0,
                flexShrink: 0,
                marginLeft: CssVars.size.gutterWidthSm,
              }}
            >
              <FoundationIcon
                color={contentColor}
                type={isLocked ? 'Lock16' : 'Dropdown16'}
              />
            </div>
          </UnstyledButton>
        )
      }}
    </ListBox>
  )
}
